import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  return (
    <header 
      className={`sticky top-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-28 py-1">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src="/images/Logo Mikhael.png" 
              alt="Mikhael Homestay Logo" 
              className="h-24 w-auto" 
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8 items-center">
            <NavLink to="/">Home</NavLink>
            <NavLink to="/#booking-platforms" isScroll={true}>Book Now</NavLink>
            <NavLink to="/house-rules">House Rules</NavLink>
            <NavLink to="/privacy-policy">Privacy Policy</NavLink>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden focus:outline-none"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            <svg 
              className="w-6 h-6" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              {isMenuOpen ? (
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M6 18L18 6M6 6l12 12" 
                />
              ) : (
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M4 6h16M4 12h16M4 18h16" 
                />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <motion.div 
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="md:hidden bg-white shadow-lg"
        >
          <nav className="flex flex-col px-4 py-4 space-y-4">
            <MobileNavLink to="/">Home</MobileNavLink>
            <MobileNavLink to="/#booking-platforms" isScroll={true}>Book Now</MobileNavLink>
            <MobileNavLink to="/house-rules">House Rules</MobileNavLink>
            <MobileNavLink to="/privacy-policy">Privacy Policy</MobileNavLink>
          </nav>
        </motion.div>
      )}
    </header>
  );
};

// NavLink component for desktop
const NavLink: React.FC<{ to: string; children: React.ReactNode; isScroll?: boolean }> = ({ to, children, isScroll = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = location.pathname === to.split('#')[0] || (to === '/' && location.pathname === '');
  
  const handleClick = (e: React.MouseEvent) => {
    if (isScroll) {
      e.preventDefault();
      const targetId = to.split('#')[1];
      
      // If we're already on the home page
      if (location.pathname === '/' || location.pathname === '') {
        const targetElement = document.getElementById(targetId);
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        // If we're on another page, navigate to home page with the hash
        navigate('/?scrollTo=' + targetId);
      }
    }
  };
  
  return (
    <Link 
      to={to} 
      className={`font-medium transition-colors ${isActive ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'}`}
      onClick={isScroll ? handleClick : undefined}
    >
      {children}
      {isActive && (
        <motion.div 
          layoutId="underline"
          className="absolute left-0 right-0 h-0.5 bg-blue-600 bottom-[-5px]"
        />
      )}
    </Link>
  );
};

// NavLink component for mobile
const MobileNavLink: React.FC<{ to: string; children: React.ReactNode; isScroll?: boolean }> = ({ to, children, isScroll = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = location.pathname === to.split('#')[0] || (to === '/' && location.pathname === '');
  
  const handleClick = (e: React.MouseEvent) => {
    if (isScroll) {
      e.preventDefault();
      const targetId = to.split('#')[1];
      
      // If we're already on the home page
      if (location.pathname === '/' || location.pathname === '') {
        const targetElement = document.getElementById(targetId);
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        // If we're on another page, navigate to home page with the hash
        navigate('/?scrollTo=' + targetId);
      }
    }
  };
  
  return (
    <Link 
      to={to} 
      className={`block py-2 px-4 rounded-md transition-colors ${isActive ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600'}`}
      onClick={isScroll ? handleClick : undefined}
    >
      {children}
    </Link>
  );
};

export default Header;
